import React, { useState } from 'react';

// Small inline icon helper (stroke icons matching the reference card).
const Icon = ({ children, className = 'h-4 w-4' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {children}
    </svg>
);

const Stat = ({ label, value }) => (
    <div className="min-w-0">
        <p className="text-[10px] font-black uppercase tracking-[0.1em] text-[#5f728a]">{label}</p>
        <p className="mt-1 truncate text-sm font-bold text-[#182233]" title={typeof value === 'string' ? value : undefined}>
            {value || '—'}
        </p>
    </div>
);

const ActionButton = ({ onClick, icon, children, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl border border-[#d8e6f3] bg-white px-3 text-sm font-black text-[#1f3350] shadow-none transition hover:border-[#9fcdf0] hover:bg-[#f2f9ff] disabled:cursor-not-allowed disabled:opacity-60"
    >
        <span className="text-[#0b74b5]">{icon}</span>
        {children}
    </button>
);

// One vehicle rendered as a full card, matching the reference Garage layout:
// photo tile + year/name/trim + active badge, a stats grid, collapsible Open diagnostics
// and Next maintenance rows, and an action row.
export default function GarageVehicleCard({
    vehicle,
    isActive,
    mileageText,
    photoUrl,
    openTickets = [],
    maintenance = { items: [], upcomingCount: 0, overdueCount: 0, unavailable: false },
    onSetActive,
    onUpdateOdometer,
    onLogService,
    onAddDocument,
    onAddPhoto,
    onRemovePhoto,
    onEditVehicle,
    onOpenTicket,
    onMaintenanceConfirm,
    onMaintenanceComplete,
    onFetchWhy,
    onFindShop,
    onOpenDetail,
}) {
    const [showDiagnostics, setShowDiagnostics] = useState(false);
    const [showMaintenance, setShowMaintenance] = useState(false);
    const [photoMenuOpen, setPhotoMenuOpen] = useState(false);
    // Which maintenance items the driver has ticked to send to a shop.
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());
    const toggleSelect = (key) => setSelectedKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    // "Why recommended?" expansion state (per maintenance item key).
    const [whyKey, setWhyKey] = useState(null);     // currently expanded item key
    const [whyCache, setWhyCache] = useState({});   // key -> explanation payload
    const [whyLoadingKey, setWhyLoadingKey] = useState(null);
    const [whyErrorKey, setWhyErrorKey] = useState(null);

    const toggleWhy = async (key) => {
        if (whyKey === key) { setWhyKey(null); return; }
        setWhyKey(key);
        setWhyErrorKey(null);
        if (whyCache[key] || whyLoadingKey === key) return;
        setWhyLoadingKey(key);
        try {
            const data = await onFetchWhy?.(key);
            if (data) setWhyCache((m) => ({ ...m, [key]: data }));
            else setWhyErrorKey(key);
        } catch (_) {
            setWhyErrorKey(key);
        } finally {
            setWhyLoadingKey((k) => (k === key ? null : k));
        }
    };

    const year = vehicle?.year || '';
    const name = [vehicle?.make, vehicle?.model].filter(Boolean).join(' ') || 'Vehicle';
    const trim = vehicle?.trim || '';
    const openCount = openTickets.length;
    const { items: maintItems = [], upcomingCount = 0, overdueCount = 0, unavailable } = maintenance;

    return (
        <section className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition ${
            isActive ? 'border-[#0b74b5] ring-1 ring-[#b9ddf8]' : 'border-[#d8e6f3]'
        }`}>
            {/* Header: photo tile + identity + active badge */}
            <div className="flex items-start gap-4 px-5 pt-5">
                <div className="relative shrink-0">
                    {photoUrl ? (
                        <div className="relative h-[76px] w-[76px] overflow-hidden rounded-xl border border-[#c6d9ec] bg-[#f6fafe]">
                            <img src={photoUrl} alt={name} className="h-full w-full object-cover" />
                            <button
                                type="button"
                                onClick={() => setPhotoMenuOpen((v) => !v)}
                                aria-label="Photo options"
                                aria-expanded={photoMenuOpen}
                                className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-[#42566c] shadow-sm transition hover:bg-white"
                            >
                                <Icon className="h-4 w-4"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></Icon>
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onAddPhoto?.(vehicle)}
                            className="group flex h-[76px] w-[76px] flex-col items-center justify-center gap-1 overflow-hidden rounded-xl border border-dashed border-[#c6d9ec] bg-[#f6fafe] text-[#5f728a] transition hover:border-[#0b74b5] hover:text-[#0b74b5]"
                            aria-label="Add vehicle photo"
                        >
                            <Icon className="h-5 w-5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></Icon>
                            <span className="text-[10px] font-black">Add photo</span>
                        </button>
                    )}

                    {photoMenuOpen && (
                        <>
                            <button
                                type="button"
                                aria-hidden="true"
                                tabIndex={-1}
                                onClick={() => setPhotoMenuOpen(false)}
                                className="fixed inset-0 z-40 cursor-default"
                            />
                            <div className="absolute left-2 top-[58px] z-50 w-56 overflow-hidden rounded-xl border border-[#e5eef6] bg-white shadow-xl">
                                <p className="border-b border-[#eef2f6] px-3 py-2 text-[11px] font-bold text-[#8ba7bf]">
                                    Vehicle photo
                                </p>
                                <button
                                    type="button"
                                    onClick={() => { setPhotoMenuOpen(false); onAddPhoto?.(vehicle); }}
                                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm font-bold text-[#182233] transition hover:bg-[#f6fafe]"
                                >
                                    <Icon className="h-4 w-4 text-[#5f728a]"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></Icon>
                                    Change photo
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setPhotoMenuOpen(false); onRemovePhoto?.(vehicle); }}
                                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm font-bold text-[#182233] transition hover:bg-[#f6fafe]"
                                >
                                    <Icon className="h-4 w-4 text-[#5f728a]"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></Icon>
                                    Remove photo
                                </button>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-xs font-bold text-[#5f728a]">{year}</p>
                        <button
                            type="button"
                            onClick={() => onOpenDetail?.(vehicle)}
                            className="block max-w-full truncate text-left text-xl font-black leading-tight text-[#0b1b2e] transition hover:text-[#0b74b5]"
                            title="View vehicle details"
                        >
                            {name}
                        </button>
                        {trim ? <p className="mt-0.5 text-sm font-semibold text-[#5f728a]">{trim}</p> : null}
                    </div>
                    {isActive ? (
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#bde7d5] bg-[#ecfff5] px-3 py-1.5 text-xs font-black text-[#087a43]">
                            <Icon className="h-3.5 w-3.5"><path d="M20 6 9 17l-5-5" /></Icon>
                            Active
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onSetActive?.(vehicle)}
                            className="shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-black text-[#0b74b5] transition hover:bg-[#eef6fc]"
                        >
                            Set active
                        </button>
                    )}
                </div>
            </div>

            {/* Stats grid */}
            <div className="mx-5 mt-4 grid grid-cols-3 gap-x-4 gap-y-4 rounded-xl border border-[#e5eef6] bg-[#fbfdff] px-4 py-4">
                <Stat label="Mileage" value={mileageText} />
                <Stat label="Engine" value={vehicle?.engine} />
                <Stat label="Transmission" value={vehicle?.transmission} />
                <Stat label="Drivetrain" value={vehicle?.drivetrain} />
                <div className="col-span-2 min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.1em] text-[#5f728a]">VIN</p>
                    <p className="mt-1 truncate font-mono text-sm font-bold text-[#182233]" title={vehicle?.vin || ''}>
                        {vehicle?.vin || '—'}
                    </p>
                </div>
            </div>

            {/* Open diagnostics (collapsible) */}
            <div className="mx-5 mt-4 overflow-hidden rounded-xl border border-[#e5eef6]">
                <button
                    type="button"
                    onClick={() => setShowDiagnostics((v) => !v)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-[#f8fbfe]"
                    aria-expanded={showDiagnostics}
                >
                    <span className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#eef6fc] text-[#0b74b5]">
                            <Icon className="h-4 w-4"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-6 6 2 2 6-6a4 4 0 0 0 5.4-5.4l-2.3 2.3-2-2z" /></Icon>
                        </span>
                        <span className="min-w-0">
                            <span className="block text-[11px] font-black uppercase tracking-[0.08em] text-[#5f728a]">Open diagnostics</span>
                            <span className="block text-sm font-black text-[#182233]">
                                {openCount} open item{openCount === 1 ? '' : 's'}
                            </span>
                        </span>
                    </span>
                    <Icon className={`h-4 w-4 shrink-0 text-[#5f728a] transition-transform ${showDiagnostics ? 'rotate-180' : ''}`}><path d="m6 9 6 6 6-6" /></Icon>
                </button>
                {showDiagnostics && (
                    <div className="border-t border-[#e5eef6] px-4 py-3">
                        {openCount === 0 ? (
                            <p className="text-sm font-semibold text-[#5f728a]">No open diagnostics for this vehicle.</p>
                        ) : (
                            <ul className="space-y-2">
                                {openTickets.map((ticket, idx) => (
                                    <li key={ticket.id ?? ticket.ticket_id ?? ticket.thread_id ?? idx}>
                                        <button
                                            type="button"
                                            onClick={() => onOpenTicket?.(ticket)}
                                            className="flex w-full items-center justify-between gap-3 rounded-lg border border-[#e5eef6] bg-white px-3 py-2 text-left transition hover:border-[#9fcdf0] hover:bg-[#f2f9ff]"
                                        >
                                            <span className="min-w-0 truncate text-sm font-bold text-[#182233]">
                                                {ticket._displayTitle || ticket.title || ticket.issue || 'Diagnostic'}
                                            </span>
                                            <Icon className="h-4 w-4 shrink-0 text-[#0b74b5]"><path d="m9 18 6-6-6-6" /></Icon>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {/* Next maintenance (collapsible) */}
            <div className="mx-5 mt-3 overflow-hidden rounded-xl border border-[#e5eef6]">
                <button
                    type="button"
                    onClick={() => setShowMaintenance((v) => !v)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-[#f8fbfe]"
                    aria-expanded={showMaintenance}
                >
                    <span className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#eef6fc] text-[#0b74b5]">
                            <Icon className="h-4 w-4"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Icon>
                        </span>
                        <span className="min-w-0">
                            <span className="block text-[11px] font-black uppercase tracking-[0.08em] text-[#5f728a]">Next maintenance</span>
                            <span className="block text-sm font-black text-[#182233]">
                                {unavailable ? 'Add mileage to see schedule' : `${upcomingCount} upcoming item${upcomingCount === 1 ? '' : 's'}`}
                            </span>
                        </span>
                    </span>
                    <span className="flex items-center gap-2">
                        {overdueCount > 0 && (
                            <span className="rounded-full border border-[#ffd2d2] bg-[#fff1f1] px-2.5 py-1 text-[11px] font-black text-[#b42318]">
                                {overdueCount} overdue
                            </span>
                        )}
                        <Icon className={`h-4 w-4 shrink-0 text-[#5f728a] transition-transform ${showMaintenance ? 'rotate-180' : ''}`}><path d="m6 9 6 6 6-6" /></Icon>
                    </span>
                </button>
                {showMaintenance && (
                    <div className="border-t border-[#e5eef6] px-4 py-3">
                        {unavailable ? (
                            <p className="text-sm font-semibold text-[#5f728a]">
                                Add this vehicle's current mileage to generate its maintenance schedule.
                            </p>
                        ) : maintItems.length === 0 ? (
                            <p className="text-sm font-semibold text-[#5f728a]">No upcoming maintenance.</p>
                        ) : (
                            <>
                            <p className="mb-2 text-xs font-semibold text-[#5f728a]">
                                Select the maintenance you want a shop to handle.
                            </p>
                            <ul className="divide-y divide-[#eef2f6] border-y border-[#eef2f6]">
                                {maintItems.map((item) => {
                                    const overdue = item.status === 'overdue';
                                    const dueSoon = item.status === 'due-soon';
                                    const dueLineClass = overdue
                                        ? 'text-[#b42318]'
                                        : dueSoon
                                        ? 'text-[#936000]'
                                        : 'text-[#5f728a]';
                                    const statusPrefix = overdue ? 'Overdue · ' : dueSoon ? 'Due soon · ' : '';
                                    const isWhyOpen = whyKey === item.key;
                                    const isWhyLoading = whyLoadingKey === item.key;
                                    const isWhyError = whyErrorKey === item.key;
                                    const why = whyCache[item.key];
                                    const isSelected = selectedKeys.has(item.key);
                                    return (
                                        <li key={item.key} className="py-3">
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleSelect(item.key)}
                                                    aria-label={`Select ${item.title} to send to a shop`}
                                                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-[#c6d9ec] accent-[#0b74b5]"
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-black leading-snug text-[#182233]">{item.title}</p>
                                                    {(statusPrefix || item.dueLabel) ? (
                                                        <p className={`mt-0.5 text-xs font-semibold ${dueLineClass}`}>
                                                            {statusPrefix}{item.dueLabel}
                                                        </p>
                                                    ) : null}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleWhy(item.key)}
                                                    className="shrink-0 whitespace-nowrap text-xs font-black text-[#0b74b5] transition hover:text-[#095d91] hover:underline"
                                                    aria-expanded={isWhyOpen}
                                                >
                                                    Why recommended?
                                                </button>
                                            </div>

                                            {isWhyOpen && (
                                                <div className="mt-2 rounded-lg border border-[#dceaf7] bg-[#f1f8ff] px-3 py-3 text-[13px] leading-relaxed text-[#2c3e55]">
                                                    <p className="mb-2 text-[11px] font-black uppercase tracking-[0.08em] text-[#1f3350]">
                                                        Why CarFido recommends this
                                                    </p>
                                                    {isWhyLoading && !why ? (
                                                        <p className="text-[#5f728a]">Generating explanation…</p>
                                                    ) : isWhyError && !why ? (
                                                        <p className="text-[#b42318]">
                                                            Couldn't load the explanation. Close and tap "Why recommended?" again to retry.
                                                        </p>
                                                    ) : why ? (
                                                        <div className="space-y-2">
                                                            {why.summary ? <p className="text-[#3a4a60]">{why.summary}</p> : null}
                                                            {why.whyItMatters ? (
                                                                <p><span className="font-black text-[#1f3350]">Why it matters: </span>{why.whyItMatters}</p>
                                                            ) : null}
                                                            {why.forYourVehicle ? (
                                                                <p><span className="font-black text-[#1f3350]">For your vehicle: </span>{why.forYourVehicle}</p>
                                                            ) : null}
                                                            {why.recordedDue ? (
                                                                <p><span className="font-black text-[#1f3350]">Recorded due: </span>{why.recordedDue}</p>
                                                            ) : null}
                                                            {why.overdueReason ? (
                                                                <p className="text-[#b42318]">{why.overdueReason}</p>
                                                            ) : null}
                                                            {why.recordedInterval ? (
                                                                <p><span className="font-black text-[#1f3350]">Recorded interval: </span>{why.recordedInterval}</p>
                                                            ) : null}
                                                            {why.lastCompleted ? (
                                                                <p><span className="font-black text-[#1f3350]">Last completed: </span>{why.lastCompleted}</p>
                                                            ) : null}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            )}

                                            {(overdue || dueSoon) && (
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => (onMaintenanceComplete
                                                            ? onMaintenanceComplete(item, vehicle?.vehicle_id)
                                                            : onMaintenanceConfirm?.(item.key, true, vehicle?.vehicle_id))}
                                                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-[12px] font-black text-white transition hover:bg-emerald-700"
                                                    >
                                                        <Icon className="h-3.5 w-3.5"><path d="M20 6 9 17l-5-5" /></Icon>
                                                        Completed
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => onMaintenanceConfirm?.(item.key, false, vehicle?.vehicle_id)}
                                                        className="rounded-full border border-[#d8e6f3] bg-white px-3 py-1.5 text-[12px] font-black text-[#5f728a] transition hover:bg-[#f8fbfe]"
                                                    >
                                                        Not completed
                                                    </button>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                            <div className="mt-3 flex items-center justify-between gap-3">
                                <span className="text-xs font-bold text-[#5f728a]">{selectedKeys.size} selected</span>
                                <button
                                    type="button"
                                    disabled={selectedKeys.size === 0}
                                    onClick={() => onFindShop?.(vehicle, maintItems.filter((it) => selectedKeys.has(it.key)))}
                                    className="inline-flex items-center gap-2 rounded-xl bg-[#0b4a6e] px-4 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-[#093a56] disabled:cursor-not-allowed disabled:bg-[#9fb3c6]"
                                >
                                    <Icon className="h-4 w-4"><path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" /></Icon>
                                    Find shop
                                </button>
                            </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2 px-5 pb-5 pt-4">
                <ActionButton
                    onClick={() => onUpdateOdometer?.(vehicle)}
                    icon={<Icon className="h-4 w-4"><circle cx="12" cy="12" r="9" /><path d="M12 12 8 8M12 7v.01" /></Icon>}
                >
                    Update odometer
                </ActionButton>
                <ActionButton
                    onClick={() => onLogService?.(vehicle)}
                    icon={<Icon className="h-4 w-4"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 13h6M9 17h3" /></Icon>}
                >
                    Log service
                </ActionButton>
                <ActionButton
                    onClick={() => onAddDocument?.(vehicle)}
                    icon={<Icon className="h-4 w-4"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M12 11v6M9 14h6" /></Icon>}
                >
                    Add document
                </ActionButton>
                <ActionButton
                    onClick={() => onEditVehicle?.(vehicle)}
                    icon={<Icon className="h-4 w-4"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></Icon>}
                >
                    Edit vehicle
                </ActionButton>
            </div>
        </section>
    );
}
